import React from 'react';
import { useTranslation } from 'react-i18next';
import CampText from '../CampText';

/**
 * Passa 1 — el futur preferit.
 *
 * La decisió que hi ha aquí a sota: **ancorar-se a un escenari és opcional i la
 * visió escrita és obligatòria**. Els escenaris de LUCIFER surten de combinar
 * els extrems dels drivers i, per construcció, la majoria no són desitjables.
 * Obligar a triar-ne un voldria dir fer backcasting cap a un futur que ningú
 * no vol. Amb els dos camps separats es pot agafar un escenari tal qual,
 * barrejar-ne dos o escriure una visió que no és cap d'ells.
 */
const VisionStep = ({ exercise, scenarios, drivers, onPatch }) => {
  const { t } = useTranslation();

  /* Quins escenaris es poden ancorar, i com es diuen.
   *
   * Dues coses que anaven malament:
   *
   * 1. **Hi sortien tots els escenaris generats.** La passa 4 en genera 2ⁿ i
   *    després en tries uns quants: els que no has triat són combinacions que
   *    has descartat, i oferir-los aquí és oferir un futur que ja has dit que
   *    no t'interessa. Ara només hi surten els triats.
   * 2. **El nom de recanvi era l'identificador de la base de dades.** Un cas
   *    amb 8 escenaris ensenyava «Escenari 19», un número que no surt enlloc
   *    més de l'aplicació. Ara és la **posició** dins del cas, que és el mateix
   *    número que veus a la passa 4.
   *
   * Si l'exercici ja estava ancorat a un escenari que després s'ha deixat de
   * triar, aquell escenari continua sortint: si no, l'ancoratge desapareixeria
   * de la pantalla sense dir res i semblaria una visió pròpia.
   */
  const ordenats = [...scenarios].sort((a, b) => a.id - b.id);
  const nomDe = (scenario) => scenario.name
    || t('scenarios.defaultName', { index: ordenats.findIndex(x => x.id === scenario.id) + 1 });
  const triables = ordenats.filter(s => s.is_selected || s.id === exercise.scenario_id);

  const valorsDelDriver = (scenario) => {
    if (!scenario.driver_values) return [];
    return Object.entries(scenario.driver_values).map(([driverId, value]) => {
      const driver = drivers.find(d => d.id.toString() === driverId);
      return {
        nom: driver?.causal_factor_name || t('narratives.unknownDriver'),
        valor: value,
        alt: value === driver?.extreme_value_high
      };
    });
  };

  const tria = (scenarioId) => {
    if (exercise.scenario_id === scenarioId) return;
    onPatch({ scenario_id: scenarioId });
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-neutral-900">{t('backcasting.step1Title')}</h3>
        <p className="mt-1 max-w-3xl text-sm text-neutral-600">{t('backcasting.step1Lead')}</p>
      </div>

      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-400">
          {t('backcasting.anchorTitle')}{' '}
          <span className="font-normal normal-case tracking-normal text-neutral-400">
            {t('backcasting.optional')}
          </span>
        </p>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {triables.map(scenario => {
            const triat = exercise.scenario_id === scenario.id;
            return (
              <button
                key={scenario.id}
                type="button"
                onClick={() => tria(scenario.id)}
                aria-pressed={triat}
                className={`rounded-lg border p-4 text-left transition-colors ${
                  triat
                    ? 'border-accent-500 bg-accent-50'
                    : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`h-3 w-3 flex-shrink-0 rounded-full border ${
                    triat ? 'border-4 border-accent-600' : 'border-neutral-300'
                  }`} />
                  <span className="text-sm font-semibold text-neutral-900">
                    {nomDe(scenario)}
                  </span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-neutral-600">
                  {valorsDelDriver(scenario).map(v => v.valor).join(' · ') || t('backcasting.noDriverValues')}
                </p>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => tria(null)}
            aria-pressed={!exercise.scenario_id}
            className={`rounded-lg border p-4 text-left transition-colors ${
              !exercise.scenario_id
                ? 'border-accent-500 bg-accent-50'
                : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={`h-3 w-3 flex-shrink-0 rounded-full border ${
                !exercise.scenario_id ? 'border-4 border-accent-600' : 'border-neutral-300'
              }`} />
              <span className="text-sm font-semibold text-neutral-900">{t('backcasting.ownVision')}</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-neutral-600">{t('backcasting.ownVisionHint')}</p>
          </button>
        </div>

        <p className="mt-2 text-xs text-neutral-500">
          {triables.length === 0
            ? t('backcasting.noSelectedScenarios')
            : t('backcasting.anchorNote')}
        </p>
      </div>

      <CampText
        label={t('backcasting.visionLabel')}
        value={exercise.vision_statement}
        onSave={(v) => onPatch({ vision_statement: v })}
        placeholder={t('backcasting.visionPlaceholder')}
        hint={t('backcasting.visionHint')}
        multiline
        rows={6}
        inputClassName="leading-relaxed"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <CampText
          label={t('backcasting.exerciseName')}
          value={exercise.name}
          onSave={(v) => onPatch({ name: v || t('backcasting.untitledExercise') })}
        />
        <CampText
          label={t('backcasting.horizon')}
          value={exercise.horizon_label}
          onSave={(v) => onPatch({ horizon_label: v })}
          hint={t('backcasting.horizonHint')}
        />
      </div>
    </div>
  );
};

export default VisionStep;
